import React, { useState, useRef } from "react";
import ModalDrawer from "@/components/structure/ModalDrawer";
import Markdown from 'react-markdown'

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface SimplePageProps {
  title?: string | React.ReactNode;
  icon?: IconProp,
  children?: React.ReactNode;
  helpTitle?: string;
  helpContent?: string;
  className?: string;
}

/**
 * A page wrapper that renders a header, the contents of the page, and an optional help drawer.
 * 
 * To supply help content, import your markdown like so:
 * import editProjectHelp from "@/assets/helpdocs/create-task.md"  with { type: "text" };
 * Then pass editProjectHelp as the `helpContent` prop to SimplePage.
 * 
 * @param {SimplePageProps} props - The properties for the SimplePage component.
 * @param {string | React.ReactNode} [props.title] - The title of the page.
 * @param {IconProp} [props.icon] - An optional FontAwesome icon to display next to the title.
 * @param {React.ReactNode} [props.children] - The main content of the page.
 * @param {string} [props.helpTitle] - The title for the help drawer.
 * @param {string} [props.helpContent] - The markdown content for the help drawer.
 * @param {string} [props.className] - Additional CSS classes for the page container.
 * @returns {JSX.Element} The rendered SimplePage component.
 */
export default function SimplePage({
  title,
  icon,
  children,
  helpTitle,
  helpContent,
  className,
}: SimplePageProps) {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <section className={`container max-w-6xl mx-auto px-4 py-8 ${className}`}>
      <h2 className="text-xl font-semibold mb-4 flex flex-grow-1 align-items-center justify-between">
        <div>
          {icon && <FontAwesomeIcon icon={icon} className="pr-2" />}
          {title}
        </div>

        {helpContent && (
          <button
            onClick={() => setHelpOpen(true)}
            className="ml-4 text-sm text-white bg-blue-600 rounded-2xl p-1 px-2 cursor-pointer"
            aria-label="Open help documentation"
          >
            <FontAwesomeIcon icon="circle-question" className="pr-1 text-white mr-1" />
            Page Help
          </button>
        )}
      </h2>

      <div className="text-gray-700 container max-w-6xl mx-auto">{children}</div>

      <ModalDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        iconTitle="circle-question"
        title={`${helpTitle || title || "Help"}`}
        widthClass="w-full max-w-md" // responsive-friendly width
      >
        { helpContent && (
          <div className="preflight max-w-none">
            <Markdown>{helpContent}</Markdown>
          </div>
        ) }
      </ModalDrawer>
    </section>
  );
}
